import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { appointmentsAPI, servicesAPI, clientsAPI, staffAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
    ArrowLeft, 
    Calendar as CalendarIcon, 
    Clock, 
    User, 
    Scissors, 
    Phone, 
    Mail,
    DollarSign,
    Edit,
    Check,
    X,
    UserX,
    Loader2,
    Save
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { formatCurrency, formatTime, getStatusColor, cn } from "@/lib/utils";

export default function AppointmentDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(searchParams.get('action') === 'reschedule');
    const [services, setServices] = useState([]);
    const [staff, setStaff] = useState([]);
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    
    const [editForm, setEditForm] = useState({
        date: null,
        time: '',
        service_id: '',
        staff_id: '',
        notes: ''
    });

    const [actionDialog, setActionDialog] = useState({
        open: false,
        action: null,
        title: '',
        description: '',
    });

    useEffect(() => {
        loadAppointment();
        loadResources();
    }, [id]);

    useEffect(() => {
        if (isEditing && editForm.date) {
            loadAvailableSlots();
        }
    }, [editForm.date, isEditing]);

    const loadAppointment = async () => {
        try {
            const res = await appointmentsAPI.getOne(id);
            const appt = res.data?.appointment || res.data;
            setAppointment(appt);
            setEditForm({
                date: appt.date ? new Date(appt.date) : null,
                time: appt.time || '',
                service_id: appt.service_id || '',
                staff_id: appt.staff_id || '',
                notes: appt.notes || ''
            });
        } catch (error) {
            console.error("Failed to load appointment:", error);
            toast.error("Failed to load appointment");
            navigate('/calendar');
        } finally {
            setLoading(false);
        }
    };

    const loadResources = async () => {
        try {
            const [servicesRes, staffRes] = await Promise.all([
                servicesAPI.getAll(),
                staffAPI.getAll()
            ]);
            setServices(servicesRes.data?.services || []);
            setStaff(staffRes.data?.staff || []);
        } catch (error) {
            console.error("Failed to load resources:", error);
        }
    };

    const loadAvailableSlots = async () => {
        if (!editForm.date) return;
        setLoadingSlots(true);
        try {
            // Generate time slots (9 AM to 6 PM)
            const generatedSlots = [];
            for (let hour = 9; hour < 18; hour++) {
                for (let minute of [0, 30]) {
                    generatedSlots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`);
                }
            }
            setSlots(generatedSlots);
        } catch (error) {
            console.error("Failed to load slots:", error);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleSave = async () => {
        if (!editForm.date || !editForm.time) {
            toast.error("Please select date and time");
            return;
        }

        setSaving(true);
        try {
            await appointmentsAPI.update(id, {
                date: format(editForm.date, 'yyyy-MM-dd'),
                time: editForm.time,
                service_id: editForm.service_id || undefined,
                staff_id: editForm.staff_id || undefined,
                notes: editForm.notes
            });
            toast.success("Appointment updated");
            setIsEditing(false);
            loadAppointment();
        } catch (error) {
            console.error("Failed to update:", error);
            toast.error("Failed to update appointment");
        } finally {
            setSaving(false);
        }
    };

    const confirmAction = (action) => {
        const configs = {
            confirm: { title: 'Confirm Appointment', description: 'Mark this appointment as confirmed?' },
            complete: { title: 'Complete Appointment', description: 'Mark this appointment as completed?' },
            cancel: { title: 'Cancel Appointment', description: 'Are you sure you want to cancel this appointment?' },
            no_show: { title: 'Mark as No Show', description: 'Mark this appointment as no-show?' },
        };
        setActionDialog({ open: true, action, ...configs[action] });
    };

    const handleAction = async () => {
        const { action } = actionDialog;
        try {
            let status;
            switch (action) {
                case 'confirm': status = 'confirmed'; break;
                case 'complete': status = 'completed'; break;
                case 'cancel': status = 'cancelled'; break;
                case 'no_show': status = 'no_show'; break;
                default: return;
            }
            
            if (action === 'cancel') {
                await appointmentsAPI.cancel(id);
            } else if (action === 'complete') {
                await appointmentsAPI.complete(id);
            } else {
                await appointmentsAPI.update(id, { status });
            }
            
            toast.success(`Appointment ${status.replace('_', ' ')}`);
            setActionDialog({ open: false, action: null, title: '', description: '' });
            loadAppointment();
        } catch (error) {
            toast.error("Failed to update appointment");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!appointment) {
        return (
            <div className="p-8 text-center">
                <p className="text-muted-foreground">Appointment not found</p>
                <Button variant="link" onClick={() => navigate('/calendar')}>
                    Back to Calendar
                </Button>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/calendar')}>
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex-1">
                    <h1 className="font-heading text-2xl md:text-3xl font-bold">
                        Appointment Details
                    </h1>
                    <p className="text-muted-foreground">
                        {format(new Date(appointment.date), 'EEEE, MMMM d, yyyy')} at {formatTime(appointment.time)}
                    </p>
                </div>
                <Badge className={cn("text-sm px-3 py-1", getStatusColor(appointment.status))}>
                    {appointment.status?.replace('_', ' ')}
                </Badge>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Main Info */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Client Card */}
                    <Card className="border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                Client Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-2xl font-bold text-primary">
                                        {appointment.client_name?.charAt(0) || '?'}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-xl font-bold">{appointment.client_name || 'Unknown Client'}</p>
                                    {appointment.client_phone && (
                                        <p className="text-muted-foreground flex items-center gap-2">
                                            <Phone className="w-4 h-4" />
                                            {appointment.client_phone}
                                        </p>
                                    )}
                                    {appointment.client_email && (
                                        <p className="text-muted-foreground flex items-center gap-2">
                                            <Mail className="w-4 h-4" />
                                            {appointment.client_email}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Service Card */}
                    <Card className="border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Scissors className="w-5 h-5 text-primary" />
                                Service Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {/* Show multiple services if available */}
                            {appointment.services && appointment.services.length > 0 ? (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        {appointment.services.map((svc, idx) => (
                                            <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-secondary/50">
                                                <div>
                                                    <p className="font-medium">{svc.name}</p>
                                                    <p className="text-sm text-muted-foreground">{svc.duration_minutes || 30} mins</p>
                                                </div>
                                                <p className="font-semibold text-primary">{formatCurrency(svc.price || 0)}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="p-4 rounded-xl bg-secondary/50">
                                            <p className="text-sm text-muted-foreground">Total Price</p>
                                            <p className="text-lg font-semibold text-primary">
                                                {formatCurrency(appointment.total_price || appointment.service_price || 0)}
                                            </p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-secondary/50">
                                            <p className="text-sm text-muted-foreground">Total Duration</p>
                                            <p className="text-lg font-semibold">{appointment.total_duration || appointment.service_duration || 30} mins</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-secondary/50">
                                            <p className="text-sm text-muted-foreground">Staff</p>
                                            <p className="text-lg font-semibold">{appointment.staff_name || 'Not assigned'}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="p-4 rounded-xl bg-secondary/50">
                                        <p className="text-sm text-muted-foreground">Service</p>
                                        <p className="text-lg font-semibold">{appointment.service_names || appointment.service_name || 'N/A'}</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-secondary/50">
                                        <p className="text-sm text-muted-foreground">Duration</p>
                                        <p className="text-lg font-semibold">{appointment.total_duration || appointment.service_duration || 30} minutes</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-secondary/50">
                                        <p className="text-sm text-muted-foreground">Price</p>
                                        <p className="text-lg font-semibold text-primary">
                                            {formatCurrency(appointment.total_price || appointment.service_price || 0)}
                                        </p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-secondary/50">
                                        <p className="text-sm text-muted-foreground">Staff</p>
                                        <p className="text-lg font-semibold">{appointment.staff_name || 'Not assigned'}</p>
                                    </div>
                                </div>
                            )}

                            {appointment.notes && (
                                <div className="mt-4 p-4 rounded-xl bg-secondary/50">
                                    <p className="text-sm text-muted-foreground">Notes</p>
                                    <p className="mt-1">{appointment.notes}</p>
                                </div>
                            )}

                            {appointment.booking_fee_paid && (
                                <div className="mt-4 p-4 rounded-xl bg-green-50 border border-green-200">
                                    <div className="flex items-center gap-2 text-green-700">
                                        <DollarSign className="w-4 h-4" />
                                        <span className="font-medium">
                                            Booking fee paid: {formatCurrency(appointment.booking_fee_amount)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Edit Form */}
                    {isEditing && (
                        <Card className="border-border/40 shadow-card border-primary">
                            <CardHeader>
                                <CardTitle className="font-heading">Reschedule Appointment</CardTitle>
                                <CardDescription>Select a new date and time</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Date</Label>
                                        <Calendar
                                            mode="single"
                                            selected={editForm.date}
                                            onSelect={(d) => setEditForm({ ...editForm, date: d, time: '' })}
                                            disabled={(date) => date < new Date()}
                                            className="rounded-xl border"
                                        />
                                    </div>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Time</Label>
                                            {loadingSlots ? (
                                                <div className="flex items-center gap-2 text-muted-foreground">
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Loading...
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
                                                    {slots.map((slot) => (
                                                        <Button
                                                            key={slot}
                                                            variant={editForm.time === slot ? 'default' : 'outline'}
                                                            size="sm"
                                                            onClick={() => setEditForm({ ...editForm, time: slot })}
                                                        >
                                                            {formatTime(slot)}
                                                        </Button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Staff (optional)</Label>
                                            <Select
                                                value={editForm.staff_id || 'none'}
                                                onValueChange={(v) => setEditForm({ ...editForm, staff_id: v === 'none' ? '' : v })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select staff" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">No preference</SelectItem>
                                                    {staff.map((s) => (
                                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Notes</Label>
                                            <Textarea
                                                value={editForm.notes}
                                                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleSave} disabled={saving}>
                                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" />
                                        Save Changes
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Actions Sidebar */}
                <div className="space-y-6">
                    <Card className="border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Quick Actions</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {!isEditing && appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <Edit className="w-4 h-4 mr-2" />
                                    Reschedule
                                </Button>
                            )}

                            {appointment.status === 'scheduled' && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => confirmAction('confirm')}
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    Confirm Appointment
                                </Button>
                            )}

                            {(appointment.status === 'scheduled' || appointment.status === 'confirmed') && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => confirmAction('complete')}
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    Mark as Completed
                                </Button>
                            )}

                            {appointment.status === 'confirmed' && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                    onClick={() => confirmAction('no_show')}
                                >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Mark as No Show
                                </Button>
                            )}

                            {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => confirmAction('cancel')}
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    Cancel Appointment
                                </Button>
                            )}
                        </CardContent>
                    </Card>

                    {/* Appointment Timeline */}
                    <Card className="border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-sm">Appointment Info</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                            <div className="flex items-center gap-2">
                                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                                <span>{format(new Date(appointment.date), 'PPP')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-muted-foreground" />
                                <span>{formatTime(appointment.time)}</span>
                            </div>
                            {appointment.booked_online && (
                                <Badge variant="secondary" className="text-xs">
                                    Booked Online
                                </Badge>
                            )}
                            <div className="pt-2 text-xs text-muted-foreground">
                                Created: {format(new Date(appointment.created_at), 'PPp')}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Action Confirmation Dialog */}
            <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, action: null, title: '', description: '' })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{actionDialog.title}</AlertDialogTitle>
                        <AlertDialogDescription>{actionDialog.description}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleAction}>Confirm</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}